import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentAcuantCamera } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingTextContentValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentAcuantCameraSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    validationErrors: DCUIThemingTextContentValidationErrors;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingTextContentAcuantCameraSection = (props: DCUIThemingTextContentAcuantCameraSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        validationErrors,
        updateDCUIContentPropertyValue
    } = props;

    const updateDCUIContentAcuantCameraTextFieldValue = (
        key: keyof DCUIContentAcuantCamera,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            acuant: {
                ...dcuiContent.acuant,
                camera: {
                    ...dcuiContent.acuant.camera,
                    [key]: value
                }
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleAcuantCameraNoneTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantCameraTextFieldValue("none", event.target.value);
    };

    const handleResetAcuantCameraNoneText = () => {
        updateDCUIContentAcuantCameraTextFieldValue("none", inheritedDCUIContent.acuant.camera.none);
    };

    const handleAcuantCameraSmallDocumentTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantCameraTextFieldValue("smallDocument", event.target.value);
    };

    const handleResetAcuantCameraSmallDocumentText = () => {
        updateDCUIContentAcuantCameraTextFieldValue("smallDocument", inheritedDCUIContent.acuant.camera.smallDocument);
    };

    const handleAcuantCameraBigDocumentTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantCameraTextFieldValue("bigDocument", event.target.value);
    };

    const handleResetAcuantCameraBigDocumentText = () => {
        updateDCUIContentAcuantCameraTextFieldValue("bigDocument", inheritedDCUIContent.acuant.camera.bigDocument);
    };

    const handleAcuantCameraTapToCaptureTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentAcuantCameraTextFieldValue("tapToCature", event.target.value);
    };

    const handleResetAcuantCameraTapToCaptureText = () => {
        updateDCUIContentAcuantCameraTextFieldValue("tapToCature", inheritedDCUIContent.acuant.camera.tapToCature);
    };

    const acuantCameraResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.acuantCamera;

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.none}
                currentThemingPropertyValue={dcuiContent.acuant.camera.none}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-acuant-camera-none-input"}
                    automationId={"document-capture-text-content-acuant-camera-none-input"}
                    label={acuantCameraResources.inputs.none.label}
                    ariaLabel={acuantCameraResources.inputs.none.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAcuantCameraNoneTextChange}
                    value={dcuiContent.acuant.camera.none || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={acuantCameraResources.inputs.none.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAcuantCameraNoneText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.cameraNoneError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.smallDocument}
                currentThemingPropertyValue={dcuiContent.acuant.camera.smallDocument}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-acuant-camera-small-document-input"}
                    automationId={"document-capture-text-content-acuant-camera-small-document-input"}
                    label={acuantCameraResources.inputs.smallDocument.label}
                    ariaLabel={acuantCameraResources.inputs.smallDocument.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAcuantCameraSmallDocumentTextChange}
                    value={dcuiContent.acuant.camera.smallDocument || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={acuantCameraResources.inputs.smallDocument.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAcuantCameraSmallDocumentText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.cameraSmallDocumentError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.bigDocument}
                currentThemingPropertyValue={dcuiContent.acuant.camera.bigDocument}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-acuant-camera-big-document-input"}
                    automationId={"document-capture-text-content-acuant-camera-big-document-input"}
                    label={acuantCameraResources.inputs.bigDocument.label}
                    ariaLabel={acuantCameraResources.inputs.bigDocument.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAcuantCameraBigDocumentTextChange}
                    value={dcuiContent.acuant.camera.bigDocument || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={acuantCameraResources.inputs.bigDocument.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAcuantCameraBigDocumentText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.cameraBigDocumentError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.acuant.camera.tapToCature}
                currentThemingPropertyValue={dcuiContent.acuant.camera.tapToCature}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"document-capture-text-content-acuant-camera-tap-to-capture-input"}
                    automationId={"document-capture-text-content-acuant-camera-tap-to-capture-input"}
                    label={acuantCameraResources.inputs.tapToCapture.label}
                    ariaLabel={acuantCameraResources.inputs.tapToCapture.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAcuantCameraTapToCaptureTextChange}
                    value={dcuiContent.acuant.camera.tapToCature || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={acuantCameraResources.inputs.tapToCapture.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAcuantCameraTapToCaptureText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.cameraTapToCaptureError}
        </div>
    );
};